import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAPI } from '../../hooks/useAPI';
import AnnouncementList from '../../components/business/AnnouncementList';
import ProductShowcase from '../../components/business/ProductShowcase';
import Loading from '../../components/ui/Loading';
import ErrorMessage from '../../components/ui/ErrorMessage';
import { getCopyrightHome } from '../../services/api/copyright';

const Copyright = () => {
  const { t, i18n } = useTranslation();
  const { data, loading, error, refetch } = useAPI(() => getCopyrightHome(), [i18n.language]);

  // 骨架屏组件
  const CopyrightSkeleton = () => (
    <div className="animate-pulse space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* 左侧骨架 */}
        <div className="bg-gray-100 rounded-lg p-4 lg:pr-4">
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 bg-white rounded-lg border border-gray-200 p-4"></div>
            ))}
          </div>
        </div>
        {/* 右侧骨架 */}
        <div className="lg:pl-4">
          <div className="h-8 bg-gray-200 rounded mb-4 w-1/3"></div>
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-white rounded-lg border border-gray-200"></div>
            ))}
          </div>
        </div>
      </div>
      {/* 产品展示骨架 */}
      <div>
        <div className="h-8 bg-gray-200 rounded mb-4 w-1/4"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-48 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    </div>
  )

  const { functionButtons = [], recentAnnouncements = [], featuredProducts = [] } = data || {};

  return (
      <div className="min-h-screen bg-gray-50">
        {/* Hero Banner - 始终显示 */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl font-bold mb-4">
              {t('copyright.title')}
            </h1>
            <p className="text-xl mb-8 text-blue-100">
              {t('copyright.subtitle')}
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* 错误状态 */}
          {error && (
            <ErrorMessage 
              message={error} 
              onRetry={refetch}
            />
          )}

          {/* 加载状态 - 骨架屏 */}
          {loading && !error && <CopyrightSkeleton />}

          {/* 正常内容 */}
          {!loading && !error && (
            <>
              {/* 主要内容区域 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                {/* 左侧：功能按钮 */}
                <div className="lg:pr-4">
                  <div className="bg-gray-100 rounded-lg p-4">
                    <h2 className="text-lg font-bold text-gray-900 mb-4 bg-gray-300 p-2 rounded">
                      {t('copyright.functions.title', '登记服务')}
                    </h2>
                    <div className="space-y-3">
                      {functionButtons.map((func) => (
                        <div key={func.id} className="function-button">
                          <a
                            href={func.url}
                            target={func.isExternal ? '_blank' : '_self'}
                            rel={func.isExternal ? 'noopener noreferrer' : undefined}
                            className="block bg-white rounded-lg border border-blue-200 p-4 hover:bg-blue-50 transition-colors duration-200"
                          >
                            <div className="flex items-center">
                              <div className="text-xl mr-3">
                                {func.icon}
                              </div>
                              <h3 className="text-sm font-medium text-gray-900">
                                {t(`copyright.functions.${func.id.replace(/-([a-z])/g, (_, c) => c.toUpperCase())}`, func.title)}
                              </h3>
                            </div>
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 右侧：登记公示 */}
                <div className="lg:pl-4">
                  <AnnouncementList 
                    announcements={recentAnnouncements} 
                    showMore={true}
                    className=""
                  />
                </div>
              </div>

              {/* 登记产品展示 */}
              <ProductShowcase 
                products={featuredProducts}
                showMore={true}
                layout="showcase"
              />
            </>
          )}
        </div>
      </div>
  );
};

export default Copyright; 